import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import type { DataOrigin } from "@/shared/contracts";
import { Icon, type IconName } from "./Icon";
import { SourceBack } from "./sceneNavigation";

/** 按钮的几种样子。叶绿按钮（leaf）已撤（UI-BASE-01a，总账 V-02）：叶绿只做进度、成功态、链接和状态点，不做按钮底；主操作一屏一个，用 primary。 */
type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  variant = "secondary",
  size,
  block,
  icon,
  loading,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm";
  block?: boolean;
  icon?: IconName;
  loading?: boolean;
}) {
  const cls = [
    "ps-btn",
    `ps-btn--${variant}`,
    size === "sm" ? "ps-btn--sm" : "",
    block ? "ps-btn--block" : "",
    !children && icon ? "ps-btn--icon" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button type="button" className={cls} aria-busy={loading || undefined} {...rest} disabled={loading || rest.disabled}>
      {icon ? <Icon name={loading ? "refresh" : icon} size={size === "sm" ? 16 : 18} /> : null}
      {children}
    </button>
  );
}

export function Card({ paper, flat, className, ...rest }: HTMLAttributes<HTMLDivElement> & { paper?: boolean; flat?: boolean }) {
  return <div className={["ps-card", paper ? "ps-card--paper" : "", flat ? "ps-card--flat" : "", className ?? ""].join(" ")} {...rest} />;
}

export type ChipTone = "neutral" | "leaf" | "sun" | "coral" | "sky" | "danger";

export function Chip({ tone = "neutral", icon, children, ...rest }: HTMLAttributes<HTMLSpanElement> & { tone?: ChipTone; icon?: IconName }) {
  return (
    <span className={`ps-chip ${tone === "neutral" ? "" : `ps-chip--${tone}`}`} {...rest}>
      {icon ? <Icon name={icon} size={13} /> : null}
      {children}
    </span>
  );
}

export function ToggleChip({ pressed, onToggle, children }: { pressed: boolean; onToggle: () => void; children: ReactNode }) {
  return (
    <button type="button" className="ps-chip" aria-pressed={pressed} onClick={onToggle}>
      {children}
    </button>
  );
}

/** 演示数据标识：fixture 数据在用户可见处明确标出，不冒充真实数据。 */
export function DataOriginBadge({ origin, label }: { origin: DataOrigin | "fixture" | "live"; label?: string }) {
  if (origin !== "fixture") return null;
  return (
    <span className="ps-origin" title="这部分是明确的演示数据，不代表真实商家、班次或账号">
      <Icon name="info" size={13} />
      {label ?? "演示数据"}
    </span>
  );
}

/**
 * 顶栏返回（用户 2026-09-24 同意）：从哪来回哪去。
 * - 有站内上一页（不是直接打开链接进来的）：普通点击退回上一页，例如从小窝的菜园门进菜园，返回回到小窝；
 * - 直接打开链接进来、没有站内历史：回上级页。back 为字符串时回它，为 true 时回地图首页。
 * 链接的 href 始终是上级页：新标签页打开、读屏、“没有死胡同”的检查都以它为准。
 * 全站二级页只用这一种顶栏（UI-BASE-01b，总账 V-03）：左边返回键 44×44、无底色图标按钮；标题一个字号（--fs-title）；可选副标题；右边最多一个文字或图标动作。
 * 标题就是这一页的 h1（S-080／RC-22：每页恰好一个 h1）。入口型页面（注册登录和入住准备几站、家人邀请）下面另有一个大标题 h1，
 * 那几页传 heading={false}，顶栏标题只是一行字。backLabel：返回键的读屏说法（默认“返回”；“我的”几页说“返回我的”）；
 * sourceBack.fallbackLabel：没有来路时返回键的读屏说法（默认同 backLabel）。
 */
export function TopBar({ title, subtitle, back, right, sourceBack, backLabel = "返回", heading = true }: { title: ReactNode; subtitle?: ReactNode; back?: string | boolean; right?: ReactNode; sourceBack?: { fallback: string; levelParent?: string; fallbackLabel?: string }; backLabel?: string; heading?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const parent = typeof back === "string" ? back : "/map";
  const hasInAppHistory = location.key !== "default";
  return (
    <header className="ps-topbar">
      {sourceBack ? (
        <SourceBack fallback={sourceBack.fallback} levelParent={sourceBack.levelParent} fallbackLabel={sourceBack.fallbackLabel ?? backLabel} className="ps-btn ps-btn--ghost ps-btn--icon"><Icon name="back" /></SourceBack>
      ) : back ? (
        <Link
          to={parent}
          className="ps-btn ps-btn--ghost ps-btn--icon"
          aria-label={backLabel}
          onClick={(event) => {
            // 修饰键 / 中键交给浏览器（新标签页打开上级页）；只有普通左键、并且确有站内上一页时才退回上一页。
            if (!hasInAppHistory || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            navigate(-1);
          }}
        >
          <Icon name="back" />
        </Link>
      ) : null}
      <div className="ps-topbar__title">
        {heading ? <h1 className="ps-topbar__h1">{title}</h1> : title}
        {subtitle ? <span className="ps-topbar__sub">{subtitle}</span> : null}
      </div>
      {right}
    </header>
  );
}

export function Page({ bare, children, className }: { bare?: boolean; children: ReactNode; className?: string }) {
  return <main className={["ps-page", bare ? "ps-page--bare" : "", className ?? ""].join(" ")}>{children}</main>;
}

export function Progress({ value, label }: { value: number; label: string }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <div className="ps-progress" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}
